/**
 * Internal shared style fragments for primitives. NOT part of the public API.
 * Everything here is token-driven (semantic tokens only — no raw hex/spacing).
 */
import type { SystemStyleObject } from '@chakra-ui/react';

/** Global focus-visible ring in the action color (keyboard only, never on mouse). */
export const focusRing: SystemStyleObject = {
  outline: 'none',
  _focusVisible: {
    outlineWidth: '2px',
    outlineStyle: 'solid',
    outlineColor: 'focusRing',
    outlineOffset: '2px',
  },
};

export type ControlSize = 'sm' | 'md' | 'lg';

/** Height / horizontal padding / font-size per control size (buttons, inputs, selects). */
export const controlSize: Record<ControlSize, SystemStyleObject> = {
  sm: { h: '8', minH: '8', px: '3', fontSize: 'sm', gap: '1.5' },
  md: { h: '10', minH: '10', px: '3.5', fontSize: 'md', gap: '2' },
  lg: { h: '11', minH: '11', px: '4.5', fontSize: 'lg', gap: '2' },
};

/** Square sizes for icon-only controls. */
export const squareSize: Record<ControlSize, SystemStyleObject> = {
  sm: { boxSize: '8', fontSize: 'md' },
  md: { boxSize: '10', fontSize: 'lg' },
  lg: { boxSize: '11', fontSize: 'xl' },
};

/**
 * Shared select-trigger styling so the native `<select>` (Select) and the custom listbox
 * button (SelectMenu) are visually IDENTICAL — same fill, border, radius, hover and focus
 * treatment. Height / padding / font come from `controlSize`; only the internals differ
 * (native chevron via padding vs. flex layout). Keep both in sync by editing here.
 */
export const selectTriggerBase: SystemStyleObject = {
  width: 'full',
  fontFamily: 'body',
  color: 'text.primary',
  bg: 'surface.default',
  borderWidth: '1px',
  borderColor: 'border.strong',
  borderRadius: 'md',
  cursor: 'pointer',
  outline: 'none',
  transitionProperty: 'border-color, box-shadow',
  transitionDuration: 'fast',
  transitionTimingFunction: 'standard',
  _hover: { borderColor: 'border.strong' },
  _disabled: { opacity: 0.55, cursor: 'not-allowed', bg: 'surface.subtle' },
};

/** The focused/open appearance shared by both triggers (soft ring + action border). */
export const selectTriggerActive: SystemStyleObject = {
  borderColor: 'action.primary',
  boxShadow: 'focus',
};

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

/** Variant color recipes — semantic tokens only; text.inverse is AA on both fills in
 *  light AND dark (white-on-blue/red in light, ink-on-brighter-blue/red in dark). */
export const buttonVariant: Record<ButtonVariant, SystemStyleObject> = {
  primary: {
    bg: 'action.primary',
    color: 'text.inverse',
    _hover: { bg: 'action.primaryHover' },
    _active: { bg: 'action.primaryActive' },
  },
  secondary: {
    bg: 'surface.default',
    color: 'text.primary',
    borderWidth: '1px',
    borderColor: 'border.default',
    _hover: { bg: 'surface.subtle', borderColor: 'border.strong' },
    _active: { bg: 'action.secondaryHover' },
  },
  ghost: {
    bg: 'transparent',
    color: 'text.primary',
    _hover: { bg: 'action.secondary' },
    _active: { bg: 'action.secondaryHover' },
  },
  danger: {
    bg: 'status.danger',
    color: 'text.inverse',
    _hover: { bg: 'status.danger', filter: 'brightness(0.92)' },
    _active: { filter: 'brightness(0.85)' },
  },
};
